import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft } from '@/lib/icons';

import {
  createFinancingPlanFormSchema,
  CreateFinancingPlanModel,
} from '@/features/financing-plans/plan';
import { DownPaymentField } from '@/features/financing-plans/DownPaymentField';
import {
  getFinancingPlanById,
  updateFinancingPlan,
} from '@/features/financing-plans/api';
import { ApiError } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/components/ui/action-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/_layout/financing-plans/$planId/edit')({
  component: EditFinancingPlanPage,
});

function EditFinancingPlanPage() {
  const { planId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['financing-plan', planId],
    queryFn: () => getFinancingPlanById(planId),
  });

  const plan = data?.value;

  const form = useForm<CreateFinancingPlanModel>({
    resolver: zodResolver(createFinancingPlanFormSchema(t)),
    values: plan
      ? {
          name: plan.name,
          description: plan.description || undefined,
          tenureMonths: plan.tenureMonths,
          markupRate: plan.markupRate,
          downPaymentPercent: plan.downPaymentPercent,
          minCreditTier: plan.minCreditTier,
          maxFinanceAmount: plan.maxFinanceAmount,
          isActive: plan.isActive,
          installmentFrequency: plan.installmentFrequency ?? 'daily',
        }
      : undefined,
  });

  // Plans saved before the down payment became mandatory can hold 0%. Flag
  // the field up front rather than letting Save be the first sign of it.
  const missingDownPayment = plan !== undefined && plan.downPaymentPercent <= 0;
  useEffect(() => {
    if (missingDownPayment) void form.trigger('downPaymentPercent');
  }, [missingDownPayment, form]);

  const onSubmit = async (values: CreateFinancingPlanModel) => {
    try {
      const result = await updateFinancingPlan(planId, values);
      if (!result.succeeded) {
        toast({
          title: t('common.error'),
          description:
            result.errors?.[0] ?? t('financingPlans.edit.updateFailed'),
          variant: 'destructive',
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['financing-plans'] });
      toast({
        title: t('common.success'),
        description: t('financingPlans.edit.planUpdated'),
      });
      navigate({ to: '/financing-plans' });
    } catch (error) {
      const description =
        error instanceof ApiError
          ? (error.errors?.[0] ?? error.message)
          : error instanceof Error
            ? error.message
            : t('financingPlans.edit.updateFailed');
      toast({
        title: t('common.error'),
        description,
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 max-w-2xl" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{t('financingPlans.edit.notFound')}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/financing-plans' })}
        >
          {t('financingPlans.edit.backToPlans')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/financing-plans' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('financingPlans.edit.title', { name: plan.name })}
        </h1>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{t('financingPlans.edit.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {missingDownPayment && (
            <Alert variant="warning" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {t('financingPlans.edit.missingDownPaymentTitle')}
              </AlertTitle>
              <AlertDescription>
                {t('financingPlans.edit.missingDownPaymentBody')}
              </AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('financingPlans.form.name')}</FormLabel>
                    <FormControl>
                      <Input placeholder="Standard 6-Month Plan" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.description')}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="tenureMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.tenureMonths')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="markupRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.markupRate')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DownPaymentField control={form.control} />
                <FormField
                  control={form.control}
                  name="maxFinanceAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.maxFinanceAmount')}
                      </FormLabel>
                      <FormControl>
                        {/* Empty means uncapped, which is a real setting the
                            backend honours — not a missing value. */}
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t('financingPlans.form.maxFinanceHint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="minCreditTier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.minCreditTier')}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="A">A - Excellent</SelectItem>
                        <SelectItem value="B">B - Good</SelectItem>
                        <SelectItem value="C">C - Fair</SelectItem>
                        <SelectItem value="D">D - Poor</SelectItem>
                        <SelectItem value="E">E - Very Poor</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="installmentFrequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.installmentFrequency')}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <FormLabel className="!mt-0">
                      {t('financingPlans.form.isActive')}
                    </FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex gap-3 pt-4">
                <ActionButton
                  type="submit"
                  loading={form.formState.isSubmitting}
                >
                  {t('common.actions.save')}
                </ActionButton>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/financing-plans' })}
                >
                  {t('common.actions.cancel')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
